import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
const primaryColors = [{
  name: 'Metro Blue',
  hex: '#1A2BFF',
  rgb: '26, 43, 255',
  usage: 'Primary brand — wordmark, key actions, signature moments'
}, {
  name: 'Canvas',
  hex: '#EDEEF1',
  rgb: '237, 238, 241',
  usage: 'Primary background — cool architectural neutral'
}, {
  name: 'Surface',
  hex: '#FFFFFF',
  rgb: '255, 255, 255',
  usage: 'Elevated surfaces — cards, panels, alt backgrounds'
}, {
  name: 'Ink',
  hex: '#0E0E12',
  rgb: '14, 14, 18, ',
  usage: 'Primary text — near-black with cool undertone'
}];
const secondaryColors = [{
  name: 'Blue',
  hex: '#1A2BFF'
}, {
  name: 'Yellow',
  hex: '#E8C547'
}, {
  name: 'Orange',
  hex: '#F26A35'
}, {
  name: 'Green',
  hex: '#2A8E47'
}, {
  name: 'Amber',
  hex: '#F4A523'
}];
const tonalPalette = [{
  name: 'Surface',
  shades: ['#FFFFFF', '#F6F7F9', '#EDEEF1', '#DDDFE5', '#C4C7D0']
}, {
  name: 'Blue',
  shades: ['#1A2BFF', '#1623D6', '#121CAD', '#0E1685', '#0A115C']
}, {
  name: 'Ink',
  shades: ['#0E0E12', '#2A2A30', '#4A4A52', '#6B6E78', '#9A9DA6']
}];
export function ColorSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [hoveredColor, setHoveredColor] = useState<string | null>(null);
  return <section id="color" ref={ref} className="min-h-screen px-8 py-24" data-id="element-104">
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="max-w-7xl mx-auto" data-id="element-105">
        <div className="mb-16" data-id="element-106">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-4" data-id="element-107">
            03 / COLOR
          </div>
          <h2 className="font-space text-5xl md:text-6xl font-medium tracking-tight" data-id="element-108">
            Color System
          </h2>
          <p className="text-lg text-[var(--text-muted)] mt-4 max-w-2xl font-helvetica" data-id="element-109">
            Metro Blue on a cool architectural canvas. Four anchors hold the
            system. Five secondary colors carry energy through transit lines,
            accents, and editorial moments.
          </p>
        </div>

        <div className="mb-4 font-mono text-xs text-[var(--text-muted)] tracking-wider" data-id="element-110">
          PRIMARY
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20" data-id="element-111">
          {primaryColors.map((color, index) => <motion.div key={color.name} initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: index * 0.1
        }} onMouseEnter={() => setHoveredColor(color.hex)} onMouseLeave={() => setHoveredColor(null)} className="group cursor-pointer" data-id="element-112">
              <motion.div className="aspect-square rounded-sm mb-4 border border-[var(--divider)]" style={{
            backgroundColor: color.hex
          }} animate={{
            scale: hoveredColor === color.hex ? 1.02 : 1,
            y: hoveredColor === color.hex ? -4 : 0
          }} transition={{
            duration: 0.3
          }} data-id="element-113" />
              <div className="space-y-2" data-id="element-114">
                <div className="font-space font-medium" data-id="element-115">{color.name}</div>
                <div className="font-mono text-xs text-[var(--text-muted)]" data-id="element-116">
                  {color.hex}
                </div>
                <div className="font-mono text-xs text-[var(--text-muted)]" data-id="element-117">
                  RGB {color.rgb}
                </div>
                <div className="text-sm text-[var(--text-muted)] mt-2 font-helvetica" data-id="element-118">
                  {color.usage}
                </div>
              </div>
            </motion.div>)}
        </div>

        <div className="mb-20" data-id="element-119">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-6" data-id="element-120">
            SECONDARY — LINE SPECTRUM
          </div>
          <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-10" data-id="element-121">
            <div className="flex flex-wrap gap-12 mb-10 justify-center md:justify-start" data-id="element-122">
              {secondaryColors.map((color, index) => <motion.div key={color.name} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={isInView ? {
              opacity: 1,
              scale: 1
            } : {}} transition={{
              duration: 0.4,
              delay: index * 0.1 + 0.2
            }} className="flex flex-col items-center gap-3" data-id="element-123">
                  <div className="w-20 h-20 rounded-full border border-[var(--divider)]" style={{
                backgroundColor: color.hex
              }} data-id="element-124" />
                  <div className="text-center" data-id="element-125">
                    <div className="font-space font-medium text-sm" data-id="element-126">
                      {color.name}
                    </div>
                    <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-127">
                      {color.hex}
                    </div>
                  </div>
                </motion.div>)}
            </div>
            <div className="space-y-3 pt-6 border-t border-[var(--divider)]" data-id="element-128">
              {secondaryColors.map(color => <div key={color.hex} className="flex items-center gap-4" data-id="element-129">
                  <div className="font-mono text-[10px] text-[var(--text-muted)] w-16" data-id="element-130">
                    {color.name}
                  </div>
                  <div className="h-3 rounded-full flex-1" style={{
                backgroundColor: color.hex
              }} data-id="element-131" />
                </div>)}
            </div>
          </div>
        </div>

        <div className="space-y-6 mb-16" data-id="element-132">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider" data-id="element-133">
            TONAL PALETTE
          </div>
          {tonalPalette.map(palette => <div key={palette.name} className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-6" data-id="element-134">
              <div className="font-mono text-xs text-[var(--text-muted)] mb-4" data-id="element-135">
                {palette.name}
              </div>
              <div className="flex gap-2" data-id="element-136">
                {palette.shades.map((shade, index) => <div key={shade} className="flex-1" data-id="element-137">
                    <div className="aspect-square rounded-sm mb-2 border border-[var(--divider)]" style={{
                backgroundColor: shade
              }} data-id="element-138" />
                    <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-139">
                      {100 - index * 20}
                    </div>
                  </div>)}
              </div>
            </div>)}
        </div>

        <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-6 mb-12" data-id="element-140">
          <div className="font-mono text-xs text-[var(--text-muted)] mb-4" data-id="element-141">
            ACCESSIBILITY
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-id="element-142">
            <div className="flex items-center gap-3" data-id="element-143">
              <div className="w-12 h-12 rounded-sm bg-[#0E0E12] flex items-center justify-center text-white text-xs font-space font-medium" data-id="element-144">
                Aa
              </div>
              <div data-id="element-145">
                <div className="font-space font-medium" data-id="element-146">Ink on Canvas</div>
                <div className="text-[var(--text-muted)] font-helvetica" data-id="element-147">
                  WCAG AAA (~19:1)
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-148">
              <div className="w-12 h-12 rounded-sm bg-[#1A2BFF] flex items-center justify-center text-white text-xs font-space font-medium" data-id="element-149">
                Aa
              </div>
              <div data-id="element-150">
                <div className="font-space font-medium" data-id="element-151">Blue on Canvas</div>
                <div className="text-[var(--text-muted)] font-helvetica" data-id="element-152">
                  WCAG AA (~6:1)
                </div>
              </div>
            </div>
          </div>
        </div>

        <div data-id="element-153">
          <div className="font-mono text-xs text-[var(--text-muted)] mb-6" data-id="element-154">
            CURRENT VS UPDATED
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-155">
            <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-6 flex gap-6 items-center" data-id="element-156">
              <div className="flex gap-2" data-id="element-157">
                <div className="w-16 h-16 rounded-sm border border-[var(--divider)] bg-[#F0EAD3] flex items-center justify-center font-mono text-[10px] text-black/40" data-id="element-158">
                  OLD
                </div>
                <div className="w-16 h-16 rounded-sm border border-[var(--divider)] bg-[#EDEEF1] flex items-center justify-center font-mono text-[10px] text-black/40" data-id="element-159">
                  NEW
                </div>
              </div>
              <div data-id="element-160">
                <div className="font-space font-medium mb-1" data-id="element-161">
                  Canvas refresh
                </div>
                <div className="text-sm text-[var(--text-muted)] font-helvetica" data-id="element-162">
                  Off the warm cream, onto a cool architectural neutral. Reads
                  as 2026 and lets the blue carry the warmth.
                </div>
              </div>
            </div>
            <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-6 flex gap-6 items-center" data-id="element-163">
              <div className="flex gap-2" data-id="element-164">
                <div className="w-16 h-16 rounded-sm border border-[var(--divider)] bg-[#1A2BFF]" data-id="element-165" />
                <div className="w-16 h-16 rounded-sm border border-[var(--divider)] bg-[#1A2BFF]" data-id="element-166" />
              </div>
              <div data-id="element-167">
                <div className="font-space font-medium mb-1" data-id="element-168">Blue holds</div>
                <div className="text-sm text-[var(--text-muted)] font-helvetica" data-id="element-169">
                  Metro Blue stays the anchor. The background change makes it
                  read more confidently against a neutral field.
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>;
}